import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Map, Download, Trash2, HardDrive, MapPin, AlertCircle, Loader2, StopCircle, Layers } from 'lucide-react';
import { toast } from 'sonner';
import { useOfflineMap } from '@/hooks/useOfflineMap';
import MapSelector from './MapSelector';

const EVENT_MIN_ZOOM = 10;
const EVENT_MAX_ZOOM = 13;

export default function OfflineMapManager() {
  const [userRegistrations, setUserRegistrations] = useState([]);
  const [loadingRegistrations, setLoadingRegistrations] = useState(true);
  const [downloadingArea, setDownloadingArea] = useState(null);

  const {
    isSupported, downloading, cancelled, progress, cacheInfo, error,
    downloadTrackArea, clearCache, cancelDownload, getCacheInfo, estimateDownloadSize
  } = useOfflineMap();

  useEffect(() => {
    const fetchRegistrations = async () => {
      try {
        const response = await axios.get(`${API}/registrations/my`);
        const regs = response.data.filter(r => {
          const eventDate = new Date(r.event?.start_date);
          return eventDate >= new Date() && (r.event?.gpx_file || r.event?.track1_file);
        });
        setUserRegistrations(regs);
      } catch (err) {
        console.error('Error fetching registrations:', err);
      } finally {
        setLoadingRegistrations(false);
      }
    };
    fetchRegistrations();
  }, []);

  useEffect(() => {
    if (downloading) {
      const interval = setInterval(getCacheInfo, 2000);
      return () => clearInterval(interval);
    }
  }, [downloading, getCacheInfo]);

  useEffect(() => {
    if (!downloading && downloadingArea) setDownloadingArea(null);
  }, [downloading, downloadingArea]);

  const handleDownloadEventArea = useCallback(async (registration) => {
    setDownloadingArea(`event_${registration.event_id}`);
    try {
      const response = await axios.get(`${API}/events/${registration.event_id}/gpx-coordinates`);
      const coordinates = response.data.coordinates;
      if (!coordinates || coordinates.length === 0) {
        toast.error('No se pudieron obtener las coordenadas del track');
        setDownloadingArea(null);
        return;
      }
      const estimate = estimateDownloadSize(coordinates, { minZoom: EVENT_MIN_ZOOM, maxZoom: EVENT_MAX_ZOOM });
      toast.info(`Descargando área del evento: ~${estimate.tileCount} tiles (~${estimate.estimatedSize})`);
      const ok = await downloadTrackArea(coordinates, { minZoom: EVENT_MIN_ZOOM, maxZoom: EVENT_MAX_ZOOM });
      if (!ok) {
        toast.error('Error al descargar el área del evento');
        setDownloadingArea(null);
      }
    } catch (err) {
      console.error('Error downloading event area:', err);
      toast.error('Error al obtener las coordenadas del evento');
      setDownloadingArea(null);
    }
  }, [downloadTrackArea, estimateDownloadSize]);

  const handleClearCache = useCallback(() => {
    if (window.confirm('¿Estás seguro de eliminar todos los mapas descargados?')) {
      clearCache();
      toast.success('Mapas offline eliminados');
    }
  }, [clearCache]);

  const handleCancelDownload = useCallback(() => {
    cancelDownload();
    toast.info('Descarga cancelada. Los tiles ya descargados se han guardado.');
  }, [cancelDownload]);

  if (!isSupported) {
    return (
      <Card className="border-0 shadow-xl bg-yellow-50" data-testid="offline-maps-unsupported">
        <CardContent className="pt-6">
          <div className="flex items-center gap-3 text-yellow-700">
            <AlertCircle className="w-6 h-6" />
            <div>
              <p className="font-semibold">Mapas Offline no disponibles</p>
              <p className="text-sm">Tu navegador no soporta Service Workers necesarios para mapas offline.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const progressPercent = progress.total > 0 ? Math.round(((progress.cached + progress.failed) / progress.total) * 100) : 0;

  return (
    <Card className="border-0 shadow-xl" data-testid="offline-maps-card">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-2xl flex items-center gap-2">
              <Map className="w-6 h-6 text-orange-500" />
              Mapas Offline
            </CardTitle>
            <CardDescription>Descarga el área de tus eventos para verla sin conexión</CardDescription>
          </div>
          <Badge variant={cacheInfo.count > 0 ? 'default' : 'secondary'} className="flex items-center gap-1">
            <HardDrive className="w-3 h-3" />
            {cacheInfo.count > 0 ? `${cacheInfo.count} tiles (${cacheInfo.formattedSize})` : 'Sin mapas'}
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2 mb-3">
            <Layers className="w-4 h-4 text-orange-500" />
            Estilo de Mapa
          </h3>
          <MapSelector />
        </div>

        <Separator />

        {downloading && (
          <div className="bg-blue-50 p-4 rounded-lg space-y-3" data-testid="download-progress">
            <div className="flex items-center justify-between text-sm">
              <span className="text-blue-700 font-medium flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                {cancelled ? 'Cancelando...' : 'Descargando mapas...'}
              </span>
              <span className="text-blue-600">{progress.cached + progress.failed} / {progress.total}</span>
            </div>
            <Progress value={progressPercent} className="h-2" />
            <div className="flex items-center justify-between">
              {progress.failed > 0 && <p className="text-xs text-orange-600">{progress.failed} tiles fallidos</p>}
              <Button size="sm" variant="destructive" onClick={handleCancelDownload} disabled={cancelled} className="ml-auto" data-testid="cancel-download-btn">
                <StopCircle className="w-4 h-4 mr-1" />
                {cancelled ? 'Cancelando...' : 'Parar Descarga'}
              </Button>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 p-3 rounded-lg text-red-700 text-sm flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            {error}
          </div>
        )}

        <div className="space-y-3">
          <h3 className="font-semibold text-gray-800 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-orange-500" />
            Áreas de Mis Eventos
          </h3>

          {loadingRegistrations ? (
            <div className="text-center py-4 text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
              <p className="text-sm">Cargando eventos...</p>
            </div>
          ) : userRegistrations.length === 0 ? (
            <div className="bg-gray-50 p-4 rounded-lg text-center" data-testid="offline-maps-no-events">
              <p className="text-sm text-gray-600">No tienes inscripciones en eventos próximos con tracks GPX</p>
              <p className="text-xs text-gray-500 mt-1">Inscríbete en un evento para descargar su área de mapa</p>
            </div>
          ) : (
            <div className="space-y-2">
              {userRegistrations.map((reg) => {
                const isDownloading = downloadingArea === `event_${reg.event_id}`;
                return (
                  <div key={reg.id} className="flex items-center justify-between bg-green-50 p-3 rounded-lg border border-green-200">
                    <div className="min-w-0 flex-1 mr-2">
                      <p className="text-sm font-medium text-gray-800 truncate">{reg.event?.title}</p>
                      <p className="text-xs text-gray-500">{reg.event?.start_date} · zoom {EVENT_MIN_ZOOM}-{EVENT_MAX_ZOOM}</p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleDownloadEventArea(reg)}
                      disabled={downloading}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid={`download-event-${reg.event_id}-btn`}
                    >
                      {isDownloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <Separator />

        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-700">Gestión del Cache</p>
            <p className="text-xs text-gray-500">
              {cacheInfo.count > 0 ? `${cacheInfo.count} tiles guardados (${cacheInfo.formattedSize})` : 'No hay mapas descargados'}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleClearCache}
            disabled={downloading || cacheInfo.count === 0}
            className="text-red-600 border-red-300 hover:bg-red-50"
            data-testid="clear-cache-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Limpiar Cache
          </Button>
        </div>

        <div className="bg-blue-50 p-3 rounded-lg">
          <p className="text-xs text-blue-700">
            <strong>Nota:</strong> el mapa offline es solo orientativo (zoom {EVENT_MIN_ZOOM}-{EVENT_MAX_ZOOM}). Para navegar con detalle usa tu GPS
            o app de navegación y activa el <strong>Modo Baliza</strong> desde "Mis Inscripciones" para que las alertas sigan funcionando.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
